<template>
  <v-container fluid v-if="value" :class="{ 'pa-0': true, 'tabbed': value.tabs && value.tabs.length > 1, 'v3-home-canvas': value.engine === 'v3' && !editMode }">
    <v-tabs v-if="value.tabs && value.tabs.length > 1" v-model="activeTab" class="mb-6 tabs" grow>
      <v-tab v-for="(tab, ti) in value.tabs" :key="'tabh' + ti">
        <v-btn small icon class="tab-move-left"
          v-if="editMode"
          @click.stop="moveTabLeft(tab)">
          <v-icon>chevron_left</v-icon>
        </v-btn>
        <div v-html="tab.title"></div>
        <div class="tab-edit-actions" v-if="editMode">
          <v-btn small icon @click="$emit('tabEdit', tab)">
            <v-icon>settings</v-icon>
          </v-btn>
          <v-btn small icon @click.stop="deleteTab(tab)">
            <v-icon>delete</v-icon>
          </v-btn>
          <v-btn small icon @click.stop="moveTabRight(tab)">
            <v-icon>chevron_right</v-icon>
          </v-btn>
        </div>
      </v-tab>
    </v-tabs>
    <v-tabs-items v-model="activeTab">
      <v-tab-item v-for="(tab, ti) in value.tabs" :key="'tabi' + ti">
        <!-- New (v3) engine: when the definition opts in and we're not in the
             builder, render the whole page with the recursive v3 renderer
             (nested rows/cols). Edit mode keeps the legacy chrome below so the
             existing drag/drop + control dialogs still work. -->
        <div
          v-if="activeTab === ti && value.engine === 'v3' && !editMode"
          class="v3-widget v3-home"
        >
          <!-- Page header — reuses the Levelling Control Centre banner class
               (lvl-pagehead), full-width above the page. No "Edit homepage"
               action for now. -->
          <div class="lvl-pagehead">
            <span class="lvl-pagehead-ic"><v-icon size="16">mdi-home-outline</v-icon></span>
            <h2>My Homepage</h2>
          </div>
          <!-- v3 canonical: the page rows live in the single container column;
               pass only it so stray legacy columns aren't rendered. -->
          <div class="v3-widget v3-pagewrap">
            <LandingV3Renderer
              :cols="tab.cols && tab.cols.length ? [tab.cols[0]] : []"
              :editMode="editMode"
              :qaActions="qaActions"
              @addDocument="addDocument"
              @sessionExpired="sessionExpired"
              @openDocument="openDocument"
            />
          </div>
        </div>
        <v-row v-else-if="activeTab === ti">
          <v-col v-for="(col, coli) in tab.cols" :key="'col' + coli" cols="12" :lg="col.cols ? col.cols : ''">
            <div class="col-edit-actions" v-if="editMode">
              <v-btn icon title="Column Settings" @click="$emit('colEdit', col)">
                <v-icon>settings</v-icon>
              </v-btn>
              <v-btn v-if="tab.cols.length > 1" icon title="Delete Col" @click="$emit('deleteCol', col)">
                <v-icon>delete</v-icon>
              </v-btn>
            </div>
            <draggable v-model="col.rows" :group="'tab' + ti" handle=".draghandle" :disabled="!editMode" tag="div" :style="editMode ? null : colMarginStyle(col, coli, tab.cols.length)" class="row controlcontainer">
              <v-col
                v-for="(r, ri) in col.rows"
                :key="'row' + ri"
                :class="['landing-row', 'py-0', editMode ? 'px-12' : '' ]"
                :style="rowMarginStyle(r)"
                v-show="editMode || ((r.controlType !== 'TaskList' || tasksLoaded) && rowHasReachableContent(r))"
                :cols="r.cols ? r.cols : '12'"
              >
                <v-icon class="draghandle" v-if="editMode">drag_indicator</v-icon>

                <div class="row-edit-actions" v-if="editMode">
                  <v-btn icon title="Control Settings" @click="$emit('rowEdit', r)">
                    <v-icon>settings</v-icon>
                  </v-btn>
                  <v-btn icon title="Delete Control" @click="$emit('deleteRow', r, col)">
                    <v-icon>delete</v-icon>
                  </v-btn>
                </div>
                <v-card :color="r.contained ? '' : 'transparent'" :class="r.contained ? 'contained' : ''">
                  <v-card-text :class="!editMode ? 'pa-0' : ''">
                    <v-row :key="'title' + ri" v-if="r.title && !['DocumentListFull', 'DonutChart', 'BarChart', 'v3Hero', 'v3QuickLinks', 'v3DonutChart', 'v3BarChart', 'v3FeedbackStrip', 'v3TaskList', 'v3TaskTable', 'v3Delegated', 'v3Empty', 'v3AdminPriority', 'v3AttentionQueue', 'v3Activity', 'v3PriorityStack', 'v3ScoreRing', 'v3LinkList', 'v3FeatureTiles', 'v3DocList', 'v3DocListFull', 'v3ActionPanel', 'v3Stepper', 'v3CoachToast', 'v3Calibration', 'v3Audit', 'v3Group'].includes(r.controlType)" class="mt-5">
                      <v-col cols="8">
                        <v-btn v-if="r.style && r.style.startsWith('collapsible')" @click="r.collapsed = !r.collapsed" text class="collapse">
                          <h2>{{ r.title }}</h2>
                          <v-icon right>{{ r.collapsed ? 'expand_more' : 'expand_less'}}</v-icon>
                        </v-btn>
                        <h2 v-else>
                          {{ r.title }}
                        </h2>
                      </v-col>

                      <v-col v-if="r.createButton" cols="4" class="d-flex justify-end">
                        <v-btn color="primary" @click="createDocument" v-if="createAction"
                          >Make an application</v-btn
                        >
                      </v-col>
                    </v-row>

                    <v-slide-y-transition>
                      <div v-show="!r.style || !r.style.startsWith('collapsible') || !r.collapsed ">
                        <div
                          :key="'CreateJD' + ri"
                          v-if="r.controlType === 'CreateDocLinks'"
                        >
                          <CreateDocLinks :editMode="editMode" @addDocument="addDocument" :documentType="r.documentType">
                          </CreateDocLinks>
                        </div>

                        <div
                          :key="'Tasks' + ri"
                          v-else-if="r.controlType === 'TaskList'"
                        >
                          <Tasks
                            :editMode="editMode"
                            :showTitle="false"
                            :fullScreen="false"
                            :filters="tasksFilters(r)"
                            :visibleColumns="r.taskListColumns"
                            @tasksLoaded="tasksLoaded = true"
                            @openDocument="openDocument"
                          >
                          </Tasks>
                        </div>

                        <div
                          class="bggradient"
                          :key="'DocList' + ri"
                          v-else-if="r.controlType === 'DocumentList'"
                        >
                          <DocumentList
                            :definition="r"
                            :editMode="editMode"
                            @openDocument="openDocument"
                          >
                          </DocumentList>
                        </div>

                        <div
                          :key="'DocList' + ri"
                          v-else-if="r.controlType === 'DocumentListFull'"
                        >
                          <DocumentListFull
                            :fullScreen="true"
                            :pageSize="0"
                            :showFooter="true"
                            :showFilters="true"
                            :filters="{ 
                              docTypeIncluded: r.docTypes ? docTypeSummary.filter(dt => r.docTypes.includes(dt.docType)) : []
                            }"
                            :sortBy="`${r.sortBy}:${r.sortDirection}`"
                            :columns="r.docListColumns"
                            :enablePreview="true"
                            :title="r.title"
                            @sessionExpired="sessionExpired"
                            @openDocument="openDocument"
                            @copyDocument="copyDocument"
                            @documentChangedHandler="documentChangedHandler"
                          >
                          </DocumentListFull>
                        </div>

                        <div v-else-if="r.controlType === 'Stepper'" :key="'stepper' + ri">
                          <LandingStepper
                            :definition="r"
                            :editMode="editMode"
                            :qaActions="qaActions"
                            @editTile="$emit('editTile', $event)"
                            @addTile="$emit('addTile', $event)"
                            @deleteTile="$emit('deleteTile', $event)"
                            @copyTile="$emit('copyTile', $event)"
                            @addDocument="addDocument"
                          >
                          </LandingStepper>
                        </div>

                        <div v-else-if="r.controlType === 'StepperSmall'" :key="'steppers' + ri">
                          <LandingStepperSmall
                            :definition="r"
                            :editMode="editMode"
                            :qaActions="qaActions"
                            @editTile="$emit('editTile', $event)"
                            @addTile="$emit('addTile', $event)"
                            @deleteTile="$emit('deleteTile', $event)"
                            @copyTile="$emit('copyTile', $event)"
                            @addDocument="addDocument"
                          >
                          </LandingStepperSmall>
                        </div>

                        <div v-else-if="r.controlType === 'LinkTilesAlt'" :key="'linksalt' + ri">
                          <LandingLinksAlt
                            :definition="r"
                            :editMode="editMode"
                            :qaActions="qaActions"
                            @editTile="$emit('editTile', $event)"
                            @addTile="$emit('addTile', $event)"
                            @deleteTile="$emit('deleteTile', $event)"
                            @copyTile="$emit('copyTile', $event)"
                            @addDocument="addDocument"
                          >
                          </LandingLinksAlt>
                        </div>

                        <div v-else-if="r.controlType === 'LinkTilesColoured'" :key="'linkscoloured' + ri">
                          <LandingLinksColoured
                            :definition="r"
                            :editMode="editMode"
                            :qaActions="qaActions"
                            @editTile="$emit('editTile', $event)"
                            @addTile="$emit('addTile', $event)"
                            @deleteTile="$emit('deleteTile', $event)"
                            @copyTile="$emit('copyTile', $event)"
                            @addDocument="addDocument"
                          >
                          </LandingLinksColoured>
                        </div>

                        <div v-else-if="r.controlType === 'QuickLinks'" :key="'quicklinks' + ri">
                          <LandingQuickLinks
                            :definition="r"
                            :editMode="editMode"
                            :qaActions="qaActions"
                            @editTile="$emit('editTile', $event)"
                            @addTile="$emit('addTile', $event)"
                            @deleteTile="$emit('deleteTile', $event)"
                            @copyTile="$emit('copyTile', $event)"
                            @addDocument="addDocument"
                          >
                          </LandingQuickLinks>
                        </div>
                        
                        <div v-else-if="r.controlType === 'Metabase'" :key="'metabase' + ri">
                          <LandingMetabaseFrame
                            :definition="r"
                            :editMode="editMode"
                            :qaActions="qaActions"
                            @editTile="$emit('editTile', $event)"
                            @addTile="$emit('addTile', $event)"
                            @deleteTile="$emit('deleteTile', $event)"
                            @copyTile="$emit('copyTile', $event)"
                          >
                          </LandingMetabaseFrame>
                        </div>

                        <div v-else-if="r.controlType === 'Chart'" :key="'chart' + ri">
                          <LandingChart
                            :definition="r"
                            :editMode="editMode"
                            :qaActions="qaActions"
                            @editTile="$emit('editTile', $event)"
                            @addTile="$emit('addTile', $event)"
                            @deleteTile="$emit('deleteTile', $event)"
                            @copyTile="$emit('copyTile', $event)"
                          >
                          </LandingChart>
                        </div>

                        <div v-else-if="r.controlType === 'FeatureTiles'" :key="'featuretiles' + ri">
                          <LandingFeatureTileRow
                            :definition="r"
                            :editMode="editMode"
                            :qaActions="qaActions"
                            @editTile="$emit('editTile', { row: r, link: $event })"
                            @addTile="$emit('addTile', { row: r })"
                            @deleteTile="$emit('deleteTile', { row: r, link: $event })"
                            @copyTile="$emit('copyTile', { row: r, link: $event })"
                            @addDocument="addDocument"
                          >
                          </LandingFeatureTileRow>
                        </div>

                        <div v-else-if="r.controlType === 'DonutChart'" :key="'donutchart' + ri">
                          <LandingDonutChart
                            :definition="r"
                            :editMode="editMode"
                            :qaActions="qaActions"
                            @sessionExpired="sessionExpired"
                          >
                          </LandingDonutChart>
                        </div>

                        <div v-else-if="r.controlType === 'BarChart'" :key="'barchart' + ri">
                          <LandingBarChart
                            :definition="r"
                            :editMode="editMode"
                            :qaActions="qaActions"
                            @sessionExpired="sessionExpired"
                          >
                          </LandingBarChart>
                        </div>

                        <!-- New-portal (v3) widgets. Styling is isolated to
                             `.v3-widget` so it doesn't affect the rest of the
                             legacy portal. Any v3 control not yet built in
                             legacy falls to the generic placeholder below. -->
                        <div v-else-if="r.controlType === 'v3Hero'" :key="'v3hero' + ri">
                          <LandingHero
                            :definition="r"
                            :editMode="editMode"
                            :qaActions="qaActions"
                            @addDocument="addDocument"
                            @openDocument="openDocument"
                          >
                          </LandingHero>
                        </div>

                        <div v-else-if="r.controlType === 'v3StatRow'" :key="'v3stat' + ri">
                          <LandingStatRow
                            :definition="r"
                            :editMode="editMode"
                            :qaActions="qaActions"
                          >
                          </LandingStatRow>
                        </div>

                        <div v-else-if="r.controlType === 'v3QuickLinks'" :key="'v3ql' + ri">
                          <LandingV3QuickLinks
                            :definition="r"
                            :editMode="editMode"
                            :qaActions="qaActions"
                          >
                          </LandingV3QuickLinks>
                        </div>

                        <div v-else-if="r.controlType === 'v3DonutChart'" :key="'v3donut' + ri">
                          <LandingV3DonutChart
                            :definition="r"
                            :editMode="editMode"
                            :qaActions="qaActions"
                          >
                          </LandingV3DonutChart>
                        </div>

                        <div v-else-if="r.controlType === 'v3BarChart'" :key="'v3bar' + ri">
                          <LandingV3BarChart
                            :definition="r"
                            :editMode="editMode"
                            :qaActions="qaActions"
                            @sessionExpired="sessionExpired"
                          >
                          </LandingV3BarChart>
                        </div>

                        <div v-else-if="r.controlType === 'v3TaskList'" :key="'v3tasks' + ri">
                          <LandingV3TaskList
                            :definition="r"
                            :editMode="editMode"
                            :qaActions="qaActions"
                            @openDocument="openDocument"
                          >
                          </LandingV3TaskList>
                        </div>

                        <div v-else-if="r.controlType === 'v3TaskTable'" :key="'v3tt' + ri">
                          <LandingV3TaskTable
                            :definition="r"
                            :editMode="editMode"
                            :qaActions="qaActions"
                            @openDocument="openDocument"
                          >
                          </LandingV3TaskTable>
                        </div>

                        <div v-else-if="r.controlType === 'v3Delegated'" :key="'v3dlg' + ri">
                          <LandingV3Delegated
                            :definition="r"
                            :editMode="editMode"
                            :qaActions="qaActions"
                            @openDocument="openDocument"
                          >
                          </LandingV3Delegated>
                        </div>

                        <div v-else-if="r.controlType === 'v3FeedbackStrip'" :key="'v3fb' + ri">
                          <LandingV3FeedbackStrip
                            :definition="r"
                            :editMode="editMode"
                            :qaActions="qaActions"
                          >
                          </LandingV3FeedbackStrip>
                        </div>

                        <div v-else-if="r.controlType === 'v3Empty'" :key="'v3empty' + ri">
                          <LandingV3Empty
                            :definition="r"
                            :editMode="editMode"
                            :qaActions="qaActions"
                            @addDocument="addDocument"
                          >
                          </LandingV3Empty>
                        </div>

                        <div v-else-if="r.controlType === 'v3AdminPriority'" :key="'v3ap' + ri">
                          <LandingV3AdminPriority
                            :definition="r"
                            :editMode="editMode"
                            :qaActions="qaActions"
                            @openDocument="openDocument"
                          >
                          </LandingV3AdminPriority>
                        </div>

                        <div v-else-if="r.controlType === 'v3AttentionQueue'" :key="'v3aq' + ri">
                          <LandingV3AttentionQueue
                            :definition="r"
                            :editMode="editMode"
                            :qaActions="qaActions"
                            @openDocument="openDocument"
                          >
                          </LandingV3AttentionQueue>
                        </div>

                        <div v-else-if="r.controlType === 'v3Activity'" :key="'v3act' + ri">
                          <LandingV3Activity
                            :definition="r"
                            :editMode="editMode"
                            :qaActions="qaActions"
                            @openDocument="openDocument"
                          >
                          </LandingV3Activity>
                        </div>

                        <div v-else-if="r.controlType === 'v3PriorityStack'" :key="'v3ps' + ri">
                          <LandingV3PriorityStack
                            :definition="r"
                            :editMode="editMode"
                            :qaActions="qaActions"
                            @openDocument="openDocument"
                          >
                          </LandingV3PriorityStack>
                        </div>

                        <div v-else-if="r.controlType === 'v3ScoreRing'" :key="'v3sr' + ri">
                          <LandingV3ScoreRing :definition="r" :editMode="editMode" :qaActions="qaActions"></LandingV3ScoreRing>
                        </div>

                        <div v-else-if="r.controlType === 'v3LinkList'" :key="'v3ll' + ri">
                          <LandingV3LinkList :definition="r" :editMode="editMode" :qaActions="qaActions"></LandingV3LinkList>
                        </div>

                        <div v-else-if="r.controlType === 'v3FeatureTiles'" :key="'v3ft' + ri">
                          <LandingV3FeatureTiles :definition="r" :editMode="editMode" :qaActions="qaActions" @addDocument="addDocument"></LandingV3FeatureTiles>
                        </div>

                        <div v-else-if="r.controlType === 'v3DocList'" :key="'v3dl' + ri">
                          <LandingV3DocList :definition="r" :editMode="editMode" :qaActions="qaActions" @openDocument="openDocument"></LandingV3DocList>
                        </div>

                        <div v-else-if="r.controlType === 'v3DocListFull'" :key="'v3dlf' + ri">
                          <LandingV3DocListFull :definition="r" :editMode="editMode" :qaActions="qaActions" @openDocument="openDocument"></LandingV3DocListFull>
                        </div>

                        <div v-else-if="r.controlType === 'v3ActionPanel'" :key="'v3ap2' + ri">
                          <LandingV3ActionPanel :definition="r" :editMode="editMode" :qaActions="qaActions" @addDocument="addDocument"></LandingV3ActionPanel>
                        </div>

                        <div v-else-if="r.controlType === 'v3Stepper'" :key="'v3st' + ri">
                          <LandingV3Stepper :definition="r" :editMode="editMode" :qaActions="qaActions"></LandingV3Stepper>
                        </div>

                        <div v-else-if="r.controlType === 'v3CoachToast'" :key="'v3ct' + ri">
                          <LandingV3CoachToast :definition="r" :editMode="editMode" :qaActions="qaActions"></LandingV3CoachToast>
                        </div>

                        <div v-else-if="r.controlType === 'v3Calibration'" :key="'v3cal' + ri">
                          <LandingV3Calibration :definition="r" :editMode="editMode" :qaActions="qaActions" @openDocument="openDocument"></LandingV3Calibration>
                        </div>

                        <div v-else-if="r.controlType === 'v3Audit'" :key="'v3aud' + ri">
                          <LandingV3Audit :definition="r" :editMode="editMode" :qaActions="qaActions"></LandingV3Audit>
                        </div>

                        <div v-else-if="r.controlType === 'v3Insights'" :key="'v3ins' + ri">
                          <LandingV3Insights :definition="r" :editMode="editMode" :qaActions="qaActions"></LandingV3Insights>
                        </div>

                        <div v-else-if="r.controlType === 'v3Group'" :key="'v3group' + ri">
                          <LandingV3Renderer
                            :cols="r.cols"
                            :editMode="editMode"
                            :qaActions="qaActions"
                            @addDocument="addDocument"
                            @sessionExpired="sessionExpired"
                            @openDocument="openDocument"
                          >
                          </LandingV3Renderer>
                        </div>

                        <div
                          v-else-if="r.controlType && r.controlType.startsWith('v3')"
                          :key="'v3ph' + ri"
                          class="v3-placeholder"
                        >
                          <v-icon left>open_in_new</v-icon>
                          <span>{{ r.title || r.controlType }} — renders in the new portal</span>
                        </div>

                        <div v-else :key="'links' + ri">
                          <LandingLinks
                            :definition="r"
                            :editMode="editMode"
                            :qaActions="qaActions"
                            @editTile="$emit('editTile', $event)"
                            @addTile="$emit('addTile', $event)"
                            @deleteTile="$emit('deleteTile', $event)"
                            @copyTile="$emit('copyTile', $event)"
                            @addDocument="addDocument"
                          >
                          </LandingLinks>
                        </div>
                      </div>
                    </v-slide-y-transition>
                  </v-card-text>
                </v-card>
              </v-col>
            </draggable>
          </v-col>
        </v-row>
      </v-tab-item>
    </v-tabs-items>
    <Loading :isVisible="hierarchiesLoading || docsLoading || isLoading" />
  </v-container>
</template>

<script>
import axios from "axios";
import draggable from "vuedraggable";
import LandingLinks from "./cLandingLinks";
import LandingLinksAlt from "./cLandingLinksAlt";
import LandingLinksColoured from "./cLandingLinksColoured";
import LandingQuickLinks from "./cLandingQuickLinks";
import LandingStepper from "./cLandingStepper";
import LandingStepperSmall from "./cLandingStepperSmall";
import LandingHero from "./cLandingHero";
import LandingStatRow from "./cLandingStatRow";
import LandingV3QuickLinks from "./cLandingV3QuickLinks";
import LandingV3DonutChart from "./cLandingV3DonutChart";
import LandingV3BarChart from "./cLandingV3BarChart";
import LandingV3FeedbackStrip from "./cLandingV3FeedbackStrip";
import LandingV3TaskList from "./cLandingV3TaskList";
import LandingV3TaskTable from "./cLandingV3TaskTable";
import LandingV3Delegated from "./cLandingV3Delegated";
import LandingV3Empty from "./cLandingV3Empty";
import LandingV3AdminPriority from "./cLandingV3AdminPriority";
import LandingV3AttentionQueue from "./cLandingV3AttentionQueue";
import LandingV3Activity from "./cLandingV3Activity";
import LandingV3PriorityStack from "./cLandingV3PriorityStack";
import LandingV3ScoreRing from "./cLandingV3ScoreRing";
import LandingV3LinkList from "./cLandingV3LinkList";
import LandingV3FeatureTiles from "./cLandingV3FeatureTiles";
import LandingV3DocList from "./cLandingV3DocList";
import LandingV3DocListFull from "./cLandingV3DocListFull";
import LandingV3ActionPanel from "./cLandingV3ActionPanel";
import LandingV3Stepper from "./cLandingV3Stepper";
import LandingV3CoachToast from "./cLandingV3CoachToast";
import LandingV3Calibration from "./cLandingV3Calibration";
import LandingV3Audit from "./cLandingV3Audit";
import LandingV3Insights from "./cLandingV3Insights";
import LandingV3Renderer from "./cLandingV3Renderer";
import { canNavigateTo } from "@/composables/useV3Nav";
import LandingMetabaseFrame from "./cLandingMetabaseFrame";
import LandingChart from "./cLandingChart";
import LandingFeatureTileRow from "./dashboard/cFeatureTileRow";
import LandingDonutChart from "./dashboard/cDonutChart";
import LandingBarChart from "./dashboard/cBarChart";
import CreateDocLinks from "./cCreateDocLinks";
import DocumentList from "./cDocumentList";
import DocumentListFull from "@/components/cDocumentsWithPartsNew";
import Tasks from "@/components/cTasks";
import { mapState } from "vuex";

export default {
  name: "cLanding",
  components: {
    draggable,
    LandingLinks,
    LandingLinksAlt,
    LandingLinksColoured,
    LandingQuickLinks,
    LandingStepper,
    LandingStepperSmall,
    LandingHero,
    LandingStatRow,
    LandingV3QuickLinks,
    LandingV3DonutChart,
    LandingV3BarChart,
    LandingV3FeedbackStrip,
    LandingV3TaskList,
    LandingV3TaskTable,
    LandingV3Delegated,
    LandingV3Empty,
    LandingV3AdminPriority,
    LandingV3AttentionQueue,
    LandingV3Activity,
    LandingV3PriorityStack,
    LandingV3ScoreRing,
    LandingV3LinkList,
    LandingV3FeatureTiles,
    LandingV3DocList,
    LandingV3DocListFull,
    LandingV3ActionPanel,
    LandingV3Stepper,
    LandingV3CoachToast,
    LandingV3Calibration,
    LandingV3Audit,
    LandingV3Insights,
    LandingV3Renderer,
    LandingMetabaseFrame,
    LandingChart,
    LandingFeatureTileRow,
    LandingDonutChart,
    LandingBarChart,
    CreateDocLinks,
    DocumentList,
    DocumentListFull,
    Tasks,
  },
  props: {
    value: { type: Object },
    qaActions: { type: Array },
    editMode: { type: Boolean },
    route: { type: String },
  },
  data: function () {
    return {
      isLoading: false,
      docType: null,
      createAction: null,
      tasksLoaded: false,
      activeTab: 0
    };
  },
  mounted() {},
  created() {
    if (!this.hierarchiesLoading) this.init();
  },
  watch: {
    hierarchiesLoading(val) {
      if (!val) this.init();
    },
    activeTab(val) {
      if (!isNaN(val)) {
        this.$emit('changeActiveTab', val);
        this.$loginState.saveSetting(this.$route.name + "_activeTab", val);
      }
    }
  },
  computed: {
    ...mapState({
      newUi: (state) => state.theme.newUi,
      docsLoading: (state) => state.docs.loading,
      docStatusSummary: (state) => state.docs.docStatusSummary,
      docTypeSummary: (state) => state.docs.docTypeSummary,
      hierarchiesLoading: (state) => state.hierarchies.loading,
      docTypes: (state) => state.hierarchies.docTypes,
    })
  },
  methods: {
    // Control-level permission gate: a link widget whose EVERY destination is
    // unreachable renders nothing at all — no dead panels (the widgets also
    // self-hide; this collapses the row wrapper/margins around them).
    rowHasReachableContent(r) {
      const reachable = (items, dest) => {
        const list = Array.isArray(items) ? items : [];
        return list.some((x) => {
          const d = dest(x);
          return !d || canNavigateTo(this, d);
        });
      };
      if (r.controlType === "v3QuickLinks") return reachable(r.tiles, (t) => t.destination);
      if (r.controlType === "v3LinkList") return reachable(r.links, (l) => l.destination || l.url);
      if (r.controlType === "v3ActionPanel") return reachable(r.actions, (a) => a.destination);
      return true;
    },
    sessionExpired(err) {
      this.$emit("sessionExpired", err);
    },
    addDocument(docType) {
      this.$emit("addDocument", docType);
    },
    openDocument(item) {
      this.$emit("openDocument", item);
    },
    copyDocument(item, tmpl_id, uploaded, action_available_id_copy, lifecycle_id_destination) {
      this.$emit("copyDocument", item, tmpl_id, uploaded, action_available_id_copy, lifecycle_id_destination);
    },
    documentChangedHandler(item) {
      this.$emit("documentChangedHandler", item);
    },
    init() {
      let activeTab = null;
      if (!this.editMode)
        activeTab = this.$loginState.getSetting(this.$route.name + "_activeTab", null);
      if (activeTab === null)
        activeTab = this.value.defaultTabIndex;

      if (isNaN(activeTab) || activeTab >= this.value.tabs.length || activeTab < 0)
        this.activeTab = 0;
      else
        this.activeTab = activeTab;

      const createDocType = this.value.createDocType || 'flex apply';
      this.docType = this.docTypes.find(dt => dt.tmpl_name.toLowerCase() === createDocType);
      if (this.docType) {
        const createOption = this.$loginState.user.createDocumentOptions.find(
          (a) => a.tmpl_name === this.docType.tmpl_name
        );
        if (createOption && createOption.lifecycles.length === 1) {
          this.createAction = createOption.lifecycles[0];
        } else if (createOption && createOption.lifecycles.length > 1) {
          this.createAction = createOption.lifecycles.find((x) => x.isDefault);
        }
      }
    },
    tasksFilters(def) {
      return {
        docTypeIncluded: this.docTypes
          .filter(dt => !def.docTypes || def.docTypes.length === 0 || def.docTypes.includes(dt.tmpl_name))
          .map(dt => { return { docType: dt.tmpl_name }})
      }
    },
    /**
     * Build inline padding style for a col.
     *
     * Horizontal margin (`marginX` or legacy `margin`) applies only to the
     * OUTER edges of the tab's column layout — left padding on the first col,
     * right padding on the last col — so adjacent cols don't double up the
     * gap between them. A single-col tab gets both outer edges.
     *
     * Vertical margin (`marginY` or legacy `margin`) applies to every col.
     *
     * Values are treated as Vuetify-style spacing units (1 = 4px) to keep
     * backward compatibility with existing definitions, but the multiplication
     * happens here so any numeric value works (not limited to 0..16).
     * Empty string / null = unset.
     */
    /**
     * Per-row margin style (top / bottom) — lets a row push the one below
     * or above it away without needing a separate Spacer control.
     * Values are px.
     */
    rowMarginStyle(r) {
      if (!r) return null;
      const unset = v => v == null || v === '';
      const style = {};
      if (!unset(r.marginTop))    style.marginTop    = `${Number(r.marginTop)}px`;
      if (!unset(r.marginBottom)) style.marginBottom = `${Number(r.marginBottom)}px`;
      return Object.keys(style).length ? style : null;
    },
    colMarginStyle(col, coli, totalCols) {
      if (!col) return null;
      const unset = v => v == null || v === '';
      const toPx = v => unset(v) ? null : `${Number(v) * 4}px`;
      const mx = !unset(col.marginX) ? col.marginX : col.margin;
      const my = !unset(col.marginY) ? col.marginY : col.margin;
      const isFirst = coli === 0;
      const isLast  = coli === (totalCols - 1);
      const style = {};
      if (!unset(mx)) {
        if (isFirst) style.paddingLeft  = toPx(mx);
        if (isLast)  style.paddingRight = toPx(mx);
      }
      if (!unset(my)) {
        style.paddingTop    = toPx(my);
        style.paddingBottom = toPx(my);
      }
      return style;
    },
    moveTabLeft(tab) {
      const currTab = this.value.tabs[this.activeTab];
      this.$emit('tabMoveLeft', tab);
      this.activeTab = this.value.tabs.indexOf(currTab);
    },
    moveTabRight(tab) {
      const currTab = this.value.tabs[this.activeTab];
      this.$emit('tabMoveRight', tab);
      this.activeTab = this.value.tabs.indexOf(currTab);
    },
    deleteTab(tab) {
      this.$emit('deleteTab', tab);
      if (this.activeTab >= this.value.tabs.length)
        this.activeTab = 0;
    },
    createDocument() {
      if (!this.docType || !this.createAction) return;

      const data = {
        recruiter: "",
        docType: this.docType.tmpl_name,
        hierarchies: [],
        docClassifiers: [],
        action: this.createAction,
      };
      this.isLoading = true;
      axios
        .post("document/createDocument/", data)
        .then((resp) => {
          if (resp.data.Status === "OK") {
            this.creationDisabled = true;
            this.$emit("documentCreated", resp.data.Data);
            this.$store.dispatch("docs/updateDoc", resp.data.Data);
          } else {
            alert(resp.data.Message);
          }
          this.response = resp.data;
          this.isLoading = false;
        })
        .catch((err) => {
          if (err.response && err.response.status === 401) {
            this.$emit("sessionExpired", err);
          } else {
            console.log(err);
            this.response = err.response
              ? err.response.data
              : { message: "Unexpected Error" };
          }
          this.isLoading = false;
        });
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/styles/vars";

.landing-row {
  position: relative;
}

.tab-move-left {
  position: absolute;
  left: 0;
  top: 0;
  display: none;
}

.tab-edit-actions {
  position: absolute;
  top: 0;
  right: 0;
  display: none;
  .v-btn {
    margin-left: -5px;
  }
}

.v-tab:hover {
  .tab-move-left, .tab-edit-actions {
    display: block;
  }
} 

::v-deep {
  .v-tabs-items, .v-tabs-bar {
    background-color: transparent !important;
  }
  .v-tab {
    border-bottom: 2px solid rgba(gray, 0.2);
    text-transform: none;
  }
}

.col-edit-actions {
  width: 100%;
  text-align: center;
}

.row-edit-actions {
  position: absolute;
  top: 15px;
  right: 20px;
  display: flex;
  flex-direction: column;
}

.controlcontainer {
  min-height: 100px;
}

.draghandle {
  position: absolute;
  top: 45%;
  left: 10px;
  cursor: move;
}

.tabbed {
  height: 100%;
  h2 {
    font-size: 18px;
  }
}

.v-btn.collapse {
  padding-left: 0;
}


.v-sheet.v-card {
  padding-top: 12px;
  box-shadow: none;
}

.v-sheet.v-card.contained {
  border-radius: 24px;
  box-shadow: 0px 4px 20px 0px rgba(0, 0, 0, .08);
  padding: 32px 40px;
  margin: 16px;
}

h2 {
  margin-bottom: 16px;
  font-family: "DM Serif Display" !important;
  font-size: 28px;
  font-weight: normal;
  line-height: 1.2em;
}

.v-btn__content > h2 {
  margin-bottom: 0;
}

.help-card {
  border-radius: 10px !important;
  h2 {
    font-size: 18px;
    font-weight: bold;
  }
}

.v-avatar {
  .headline {
    font-weight: bold;
  }
  &.red {
    color: #bf012f;
    background-color: rgba(#bf012f, 0.2) !important;
  }
  &.purple {
    color: #6938b9;
    background-color: rgba(#6938b9, 0.2) !important;
  }
  &.orange {
    color: #cf8a38;
    background-color: rgba(#cf8a38, 0.2) !important;
  }
}

.chevron.right {
  box-sizing: border-box;
  position: relative;
  display: block;
  transform: scale(var(--ggs, 1));
  width: 65px;
  height: 120px;
  border: 1px solid transparent;
  border-radius: 100px;
}
.chevron.right::after {
  content: "";
  display: block;
  box-sizing: border-box;
  position: absolute;
  width: 60px;
  height: 60px;
  border-bottom: 1px solid;
  border-right: 1px solid;
  transform: rotate(-45deg);
  right: 20px;
  top: 30px;
}
.chevron.right.chevron-red::after {
  border-color: rgba(#bf012f, 0.5);
}
.chevron.right.chevron-purple::after {
  border-color: rgba(#6938b9, 0.5);
}

.theme--light .bggradient {
  border-top: 1px solid #eee;
  margin-left: -64px;
  margin-right: -64px;
  padding-left: 32px;
  padding-right: 32px;
  background: linear-gradient(180deg, #ffffff 0%, $background-color-light 100%);
  min-height: 50vh;
}

.theme--light .tabbed {
  background-color: white;
}

.v3-placeholder {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 20px 24px;
  border: 1px dashed rgba(gray, 0.4);
  border-radius: 12px;
  color: rgba(gray, 0.9);
  font-size: 14px;
  background: rgba(gray, 0.04);
}

</style>